import { useState, type FormEvent } from 'react';
import {
  AVATARS,
  MIN_PLAYERS,
  MAX_PLAYERS,
  normaliseRoomCode,
  type AvatarId
} from '@crew/shared';
import type { Crew } from '../net.js';
import { HeroScene } from './Art.js';

export function Home({ crew, onOpenManual }: { crew: Crew; onOpenManual: () => void }) {
  const [name, setName] = useState(() => window.localStorage.getItem('crew-online:name') ?? '');
  const [avatar] = useState<AvatarId>(() => remembered('avatar', AVATARS));
  const [code, setCode] = useState(() =>
    normaliseRoomCode(new URLSearchParams(window.location.search).get('room') ?? '')
  );
  const [busy, setBusy] = useState(false);
  const invited = code.length === 4;

  const remember = (key: string, value: string) => {
    try {
      window.localStorage.setItem(`crew-online:${key}`, value);
    } catch {
      /* private browsing */
    }
  };

  const submit = async (event: FormEvent, mode: 'create' | 'join') => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (mode === 'create') await crew.createRoom(name, avatar);
      else await crew.joinRoom(normaliseRoomCode(code), name, avatar);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="home">
      <div className="home__hero">
        <div className="home__header">
          <h1>
            <span className="home__title-kicker">Crew</span>
            <span className="home__title-main">Quest for Planet Nine</span>
          </h1>
          <p className="home__tagline">
            A co-operative trick-taking mission for {MIN_PLAYERS}&ndash;{MAX_PLAYERS} pilots, out past
            Neptune in search of the ninth planet.
            <strong> You all win, or you all fail.</strong>
          </p>
        </div>
        <HeroScene />
      </div>

      {invited ? (
        <p className="home__invited">
          You were invited to room <strong>{code}</strong>. Pick a name and jump in.
        </p>
      ) : null}

      <div className="panel home__identity">
        <div className="identity__name">
          <label htmlFor="pilot-name">Your name</label>
          <input
            id="pilot-name"
            value={name}
            maxLength={16}
            placeholder="Nova"
            onChange={(e) => {
              setName(e.target.value);
              remember('name', e.target.value);
            }}
            autoComplete="nickname"
          />
        </div>
      </div>

      <div className="home__panels">
        <form className="panel" onSubmit={(e) => submit(e, 'create')}>
          <h2>Start a crew</h2>
          <button
            className="btn btn--primary btn--big"
            type="submit"
            disabled={busy || !name.trim()}
          >
            Open a room
          </button>
          <p className="hint">
            {name.trim()
              ? 'You get a 4-letter code to share with your friends.'
              : 'Enter your name to continue.'}
          </p>
        </form>

        <form className="panel" onSubmit={(e) => submit(e, 'join')}>
          <h2>Join a crew</h2>
          <label htmlFor="room-code">Room code</label>
          <input
            id="room-code"
            value={code}
            maxLength={4}
            placeholder="K7QP"
            className="input--code"
            onChange={(e) => setCode(normaliseRoomCode(e.target.value))}
            autoComplete="off"
            spellCheck={false}
          />
          <button
            className="btn btn--big"
            type="submit"
            disabled={busy || !name.trim() || code.length < 4}
          >
            Join room
          </button>
          {name.trim() ? null : <p className="hint">Enter your name to continue.</p>}
        </form>
      </div>

      <section className="home__rules" aria-labelledby="briefing-heading">
        <h2 id="briefing-heading">Twenty second briefing</h2>
        <ul>
          <li>All 40 cards are dealt out. Rockets beat every colour.</li>
          <li>Follow the led colour if you can. Highest card of it wins the trick.</li>
          <li>Win the trick holding your own task card. Miss one and the mission ends.</li>
        </ul>
        <button className="btn btn--ghost" onClick={onOpenManual}>
          Read the full manual
        </button>
      </section>
    </div>
  );
}

function remembered<T extends string>(key: string, allowed: readonly T[]): T {
  const saved = window.localStorage.getItem(`crew-online:${key}`);
  if ((allowed as readonly string[]).includes(saved ?? '')) return saved as T;
  return allowed[Math.floor(Math.random() * allowed.length)] as T;
}
